import React, { useState, useEffect, useRef } from "react";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { handleGetRequest } from "../../../services/GetTemplate";
import { handlePutRequest } from "../../../services/PutTemplate";
import Loading from "../../../components/Loader";
import classNames from "classnames";
import SecuredFileChange from "../../../components/SecuredFileChange/SecuredFileChange";
import { Dialog } from "primereact";
import { Carousel } from "primereact/carousel";
import { MultiSelect } from "primereact/multiselect";
import { InputTextarea } from "primereact/inputtextarea";
import baseURL, { endPoints } from "../../../Config";

const EditViewParts = ({ onHide, getPartList, partsdataId, isEditable }) => {
    const [dimension, setDimension] = useState([]);
    const [variant, setVariant] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedFileArray, setSelectedFileArray] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [selectedFileError, setSelectedFileError] = useState(null);
    const [loadingBtn, setLoadingBtn] = useState(false);
    const [displayImageDialog, setDisplayImageDialog] = useState(false);
    const [shippingData, setShippingData] = useState([]);
    const [brandData, setBrandData] = useState([]);
    const [conditionData, setConditionData] = useState([]);

    const partImageRef = useRef(null);
    const dispatch = useDispatch();

    const getPartById = async () => {
        setLoading(true);
        const response = await dispatch(handleGetRequest(endPoints.getPartById(partsdataId), false));
        if (response?.statusCode === 200) {
            const keyData = response?.data;
            formik.setFieldValue("id", keyData?.id);
            formik.setFieldValue("partName", keyData?.partName);
            formik.setFieldValue("skuNo", keyData?.skuNo);
            formik.setFieldValue("dimensionsId", keyData?.dimensionName);

            let variantids = [];
            if (Array.isArray(keyData?.variantIds)) {
                variantids = keyData.variantIds.flatMap((item) => (typeof item === "string" ? item.split(",") : item)).filter((id) => id && id.trim() !== ""); // remove empty strings
            }

            formik.setFieldValue("variantId", variantids);
            formik.setFieldValue("quantity", keyData?.quantity ? keyData?.quantity : "");
            formik.setFieldValue("cost", keyData?.price);
            formik.setFieldValue("currencyCode", keyData?.currencyCode);
            formik.setFieldValue("makerId", keyData?.makerId);
            let shippingIds = [];
            if (Array.isArray(keyData?.shippingIds)) {
                shippingIds = keyData.shippingIds.flatMap((item) => (typeof item === "string" ? item.split(",") : item)).filter((id) => id && id.trim() !== ""); // remove empty strings
            }

            formik.setFieldValue("shippingId", shippingIds);
            formik.setFieldValue("brand", keyData?.brand);
            formik.setFieldValue("condition", keyData?.condition);
            formik.setFieldValue("description", keyData?.description);
            formik.setFieldValue("isActive", keyData?.partIsActive ? true : false);
            setSelectedFileArray(
                keyData?.images?.map((item) => {
                    return `${baseURL}/${item?.imgPath}`;
                }) ?? []
            );
        }
        setLoading(false);
    };

    useEffect(() => {
        if (partsdataId) {
            getPartById();
        }
    }, [partsdataId]);

    const validationSchema = Yup.object().shape({
        skuNo: Yup.string()
            .matches(/^[^\s]+(\s[^\s]+)*$/, "Leading and trailing space is not allowed")
            .max(8, "Part name must be at most 8 characters")
            .required("This field is required"),
        quantity: Yup.string()?.required("Quantity should be atleast 1"),
        partName: Yup.string()
            .matches(/^[^\s]+(\s[^\s]+)*$/, "Leading and trailing space is not allowed")
            .max(30, "Part name must be at most 30 characters")
            .required("This field is required"),
        cost: Yup.string()?.required("This field is required").nullable(),
        currencyCode: Yup.string()?.required("This field is required"),
        images: Yup.string().when([], {
            is: () => selectedFileArray.length === 0,
            then: Yup.string().required("You must upload at least one image"),
            otherwise: Yup.string(),
        }),
    });
    const formik = useFormik({
        validationSchema,
        initialValues: {
            id: "",
            skuNo: "",
            quantity: "1",
            partName: "",
            dimensionsId: "",
            shippingId: [],
            brand: "",
            description: "",
            condition: "",
            variantId: [],
            cost: "",
            currencyCode: "",
            images: "",
            videoLink: "",
            isActive: "",
        },

        onSubmit: async (data) => {
            setLoading(true);
            setLoadingBtn(true);

            const newBase64Array = selectedFileArray.filter((item) => item.startsWith("data:image"));
            const updatedSelectedFileArray = selectedFileArray.filter((item) => !item.startsWith("data:image")).map((url) => url.replace(`${baseURL}/`, ""));
            let obj = {
                id: data?.id,
                skuNo: data?.skuNo,
                quantity: parseInt(data?.quantity),
                partName: data?.partName,
                condition: data?.condition,
                shippingIds: data?.shippingId,
                brand: data?.brand,
                description: data?.description,
                dimensionsId: data?.dimensionsId ? data?.dimensionsId?.toString() : "",
                variantIds: data?.variantId ? data?.variantId : undefined,
                cost: data?.cost ? data?.cost : undefined,
                videoLink: data?.videoLink ? data?.videoLink : "",
                currencyCode: data?.currencyCode ? data?.currencyCode?.toString() : undefined,
                newImages: newBase64Array ? newBase64Array : null,
                oldImages: updatedSelectedFileArray ? updatedSelectedFileArray : null,
            };

            const response = await dispatch(handlePutRequest(obj, endPoints.updatePart, false, true));
            if (response?.statusCode == 200) {
                await getPartList();
                onHide();
            }
            setLoading(false);
            setLoadingBtn(false);
        },
    });

    const getDimension = async () => {
        const response = await dispatch(handleGetRequest(endPoints.getallDimensions, false));
        if (response) {
            setDimension(response?.data);
        }
    };

    const getVariant = async () => {
        const response = await dispatch(handleGetRequest(endPoints.getVariantsDropdown, false));
        if (response) {
            setVariant(response?.data);
        }
    };

    const getCurrencies = async () => {
        const response = await dispatch(handleGetRequest(endPoints.getCurrenciesDropdown, false));
        if (response?.statusCode == 200) {
            setCurrencies(response?.data);
        }
    };

    const getShipping = async () => {
        const response = await dispatch(handleGetRequest(endPoints.getallShipments, false));
        if (response?.statusCode == 200) {
            setShippingData(response?.data);
        }
    };

    const getBrand = async () => {
        const response = await dispatch(handleGetRequest(endPoints.getallBrands, false));
        if (response?.statusCode == 200) {
            setBrandData(response?.data);
        }
    };
    const getConditions = async () => {
        const response = await dispatch(handleGetRequest(endPoints.getallConditions, false));
        if (response?.statusCode == 200) {
            setConditionData(response?.data);
        }
    };

    useEffect(() => {
        getDimension();
        getVariant();
        getCurrencies();
        getShipping();
        getBrand();
        getConditions();
    }, []);

    const previewImage = () => {
        setDisplayImageDialog(true);
    };
    const closeDisplayDialog = () => {
        setDisplayImageDialog(false);
    };

    const handlePartIcon = () => {
        partImageRef.current.click();
    };

    const handleDeleteImage = (index) => {
        setSelectedFileArray((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };

    const responsiveOptions = [
        {
            breakpoint: "1024px",
            numVisible: 3,
            numScroll: 3,
        },
    ];

    const imagePreviewer = (file) => {
        return (
            <div className="p-field col-12 md:col-12">
                <img src={file} style={{ maxWidth: "100%", maxHeight: "100%", width: "900px", height: "500px", objectFit: "contain" }} />
            </div>
        );
    };

    function handlePartImageChange(event) {
        setSelectedFileError("");
        if (selectedFileArray?.length >= 6) {
            setSelectedFileError("You can only upload a maximum of 6 images.");
            return;
        }
        const selectedFile = event.target.files[0];
        const fileTypes = ["image"];
        const { file: newFile, error } = SecuredFileChange(selectedFile, fileTypes);
        if (error) {
            setSelectedFileError(error);
        } else if (newFile) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result;
                setSelectedFileArray((prevItems) => [...prevItems, base64String]);
            formik.setFieldValue("images", "uploaded");
            formik.setFieldTouched("images", false);
            };
            reader.readAsDataURL(newFile);
        }
    }

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;
    };

    return (
        <>
            <Dialog visible={displayImageDialog} blockScroll style={{ width: "50vw" }} maskClassName="zindex9999" onHide={() => closeDisplayDialog()} draggable={false}>
                {selectedFileArray.length > 1 ? (
                    <Carousel value={selectedFileArray || []} numVisible={1} numScroll={1} responsiveOptions={responsiveOptions} className="custom-carousel" circular={false} itemTemplate={imagePreviewer} />
                ) : (
                    <img src={selectedFileArray[0]} style={{ maxWidth: "100%", maxHeight: "100%", width: "900px", height: "500px", objectFit: "contain" }} />
                )}
            </Dialog>

            {loading ? <Loading /> : null}

            <form onSubmit={formik.handleSubmit}>
                <div className="p-fluid formgrid grid pl-2 pr-2">
                    <div className="field col-12 md:col-6">
                        <label> Part Name </label>
                        <span className="Label__Required">*</span>
                        <InputText className={classNames({ "p-invalid": isFormFieldValid("partName") }, "Input__Round")} id="partName" name="partName" type="text" maxLength={25} value={formik.values.partName} onChange={formik.handleChange} disabled={!isEditable} placeholder="Enter Part Name" />
                        {getFormErrorMessage("partName")}
                    </div>

                    <div className="field col-12 md:col-6">
                        <label> SKU Number </label>
                        <span className="Label__Required">*</span>
                        <InputText className={classNames({ "p-invalid": isFormFieldValid("skuNo") }, "Input__Round")} id="skuNo" name="skuNo" type="text" maxLength={25} value={formik.values.skuNo} onChange={formik.handleChange} disabled={!isEditable} placeholder="Enter SKU Number" />
                        {getFormErrorMessage("skuNo")}
                    </div>
                    <div className="field col-12 md:col-6">
                        <label> Quantity </label>
                        <span className="Label__Required">*</span>
                        <InputText className={classNames({ "p-invalid": isFormFieldValid("quantity") }, "Input__Round")} id="quantity" name="quantity" type="text" maxLength={7} value={formik.values.quantity} onChange={formik.handleChange} disabled={!isEditable} placeholder="Enter Quantity" />
                        {getFormErrorMessage("quantity")}
                    </div>
                    <div className="field col-12 md:col-6">
                        <label> Variant </label>
                        <span className="Label__Required"></span>
                        <MultiSelect 
                            appendTo={"self"}
                            className={classNames({ "p-invalid": isFormFieldValid("variantId") }, "Dropdown__Round")}
                            id="variantId"
                            name="variantId"
                            options={variant}
                            optionValue="id"
                            optionLabel="name"
                            filter
                            showClear={!!formik?.values?.variantId}
                            filterBy="variantName"
                            showFilterClear={true}
                            resetFilterOnHide={true}
                            onChange={(e) => {
                                formik?.setFieldValue("variantId", e.target.value ? e.target.value : "");
                            }}
                            value={formik.values.variantId}
                            disabled={!isEditable}
                            placeholder="Select Variant"
                        />
                        {getFormErrorMessage("variantId")}
                    </div>

                    <div className="field col-12 md:col-6">
                        <label> Dimensions </label>
                        <span className="Label__Required"></span>
                        <Dropdown 
                            appendTo={"self"}
                            className={classNames({ "p-invalid": isFormFieldValid("dimensionsId") }, "Dropdown__Round")}
                            id="dimensionsId"
                            name="dimensionsId"
                            options={dimension}
                            optionValue="id"
                            optionLabel="name"
                            filter
                            showClear={!!formik?.values?.dimensionsId}
                            filterBy="dimension"
                            showFilterClear={true}
                            resetFilterOnHide={true}
                            onChange={(e) => {
                                formik?.setFieldValue("dimensionsId", e.target.value ? e.target.value : "");
                            }}
                            value={formik.values.dimensionsId}
                            disabled={!isEditable}
                            maxLength={30}
                            placeholder="Select"
                        />
                        {getFormErrorMessage("dimensionsId")}
                    </div>
                    <div className="flex field col-12 md:col-6">
                        <div className="field col-12 md:col-6 ">
                            <label> Price </label>
                            <span className="Label__Required">*</span>
                            <InputText
                                keyfilter={/^[0-9]*(\.[0-9]*)?$/}
                                className={classNames({ "p-invalid": isFormFieldValid("cost") }, "Input__Round")}
                                id="cost"
                                maxLength={8}
                                name="cost"
                                type="text"
                                value={formik.values.cost}
                                onChange={formik.handleChange}
                                placeholder="Enter Price"
                                disabled={!isEditable}
                            />
                            {getFormErrorMessage("cost")}
                        </div>
                        <div className="field col-12 md:col-6 ml-0">
                            <label> Currency </label>
                            <span className="Label__Required">*</span>
                            <Dropdown 
                                appendTo={"self"}
                                placeholder="Select Currency"
                                className={classNames({ "p-invalid": isFormFieldValid("currencyCode") }, "Dropdown__Round")}
                                id="currencyCode"
                                name="currencyCode"
                                options={currencies}
                                optionValue="name"
                                optionLabel="name"
                                value={formik.values.currencyCode}
                                filter
                                showClear={!!formik?.values?.currencyCode}
                                filterBy="name"
                                showFilterClear={true}
                                resetFilterOnHide={true}
                                onChange={(e) => {
                                    formik?.setFieldValue("currencyCode", e.target.value ? e.target.value : "");
                                }}
                                disabled={!isEditable}
                            />
                            {getFormErrorMessage("currencyCode")}
                        </div>
                    </div>
                    <div className="field col-12 md:col-6">
                        <label> Shipping </label>
                        <span className="Label__Required"></span>
                        <MultiSelect 
                            appendTo={"self"}
                            className={classNames({ "p-invalid": isFormFieldValid("shippingId") }, "Dropdown__Round")}
                            id="shippingId"
                            name="shippingId"
                            options={shippingData}
                            optionValue="id"
                            optionLabel="name"
                            filter
                            showClear={!!formik?.values?.shippingId}
                            filterBy="shippingMethod"
                            showFilterClear={true}
                            resetFilterOnHide={true}
                            onChange={(e) => {
                                formik?.setFieldValue("shippingId", e.target.value ? e.target.value : "");
                            }}
                            value={formik.values.shippingId}
                            placeholder="Select"
                            disabled={!isEditable}
                        />
                        {getFormErrorMessage("shippingId")}
                    </div>

                    <div className="field col-12 md:col-6">
                        <label> Brand </label>
                        <span className="Label__Required"></span>
                        <Dropdown 
                            appendTo={"self"}
                            className={classNames({ "p-invalid": isFormFieldValid("brand") }, "Dropdown__Round")}
                            id="brand"
                            name="brand"
                            options={brandData}
                            optionValue="id"
                            optionLabel="name"
                            filter
                            showClear={!!formik?.values?.brand}
                            filterBy="name"
                            showFilterClear={true}
                            resetFilterOnHide={true}
                            onChange={(e) => {
                                formik?.setFieldValue("brand", e.target.value ? e.target.value : "");
                            }}
                            value={formik.values.brand}
                            placeholder="Select"
                            disabled={!isEditable}
                        />
                        {getFormErrorMessage("brand")}
                    </div>
                    <div className="field col-12 md:col-6">
                        <label> Condition </label>
                        <span className="Label__Required"></span>
                        <Dropdown 
                            appendTo={"self"}
                            className={classNames({ "p-invalid": isFormFieldValid("condition") }, "Dropdown__Round")}
                            id="condition"
                            name="condition"
                            options={conditionData}
                            optionValue="name"
                            optionLabel="name"
                            filterBy="condition"
                            showClear={!!formik?.values?.condition}
                            showFilterClear={true}
                            resetFilterOnHide={true}
                            onChange={formik.handleChange}
                            value={formik.values.condition}
                            placeholder="Select"
                            disabled={!isEditable}
                        />
                        {getFormErrorMessage("condition")}
                    </div>

                    <div className="field col-12 md:col-12">
                        <label> Description </label>
                        <InputTextarea
                            rows={4}
                            className={classNames({ "p-invalid": isFormFieldValid("description") }, "Input__Round")}
                            id="description"
                            name="description"
                            type="text"
                            value={formik.values.description}
                            onChange={formik.handleChange}
                            placeholder="Enter Description"
                            disabled={!isEditable}
                        />
                    </div>

                    {isEditable && (
                        <div className="field col-12 md:col-6">
                            <label> Upload Images </label> <span className="Label__Required">*</span>
                            <div className="file-input-container " onClick={handlePartIcon}>
                                <input type="file" accept="image/*" style={{ display: "none" }} ref={partImageRef} onChange={handlePartImageChange} disabled={!isEditable || selectedFileArray?.length >= 6} />
                                <input className={classNames({ "p-invalid": isFormFieldValid("images") }, "custom-file p-inputtext p-component Input__Round")} type="text" placeholder="Choose a file..." value={formik.values.images} readOnly disabled={!isEditable || selectedFileArray?.length >= 6} />
                                <span className="file-upload-icon">
                                    <i className="pi pi-paperclip" />
                                </span>
                            </div>
                            {selectedFileError ? <small className="p-error">{selectedFileError}</small> : getFormErrorMessage("images")}
                        </div>
                    )}

                    <div className="field col-12 md:col-12">
                        <div>
                            <label> Uploaded Images </label>
                        </div>

                        <div className=" border-solid border-900 border-2 border-round p-5 mt-2">
                            {selectedFileArray.map((item, index) => {
                                return (
                                    <div key={index} style={{ position: "relative", display: "inline-block", margin: "5px", width: "110px", height: "110px" }}>
                                        {isEditable ? <i className="pi pi-times closingIcon" onClick={() => handleDeleteImage(index)} style={{ cursor: "pointer", position: "absolute", top: "0", right: "0" }} /> : ""}
                                        <img
                                            src={item}
                                            style={{ width: "90px", height: "90px", objectFit: "contain" }}
                                            alt="Part Image"
                                            onClick={() => {
                                                previewImage();
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div className="col-12 d-flex flex-row text-center mt-4 pb-2">
                        <Button className="Cancel-Button" label="Cancel" type="button" onClick={onHide} />
                        {isEditable === true && <Button className="Save-Button ml-2" label="Update" type="submit" loading={loadingBtn} />}
                    </div>
                </div>
            </form>
        </>
    );
};

export default EditViewParts;
